"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  alphaOptions,
  ExampleButton,
  levelOptions,
} from "@/components/tools/stats-tests/kit";
import {
  EMPTY_SUMMARY,
  readSummary,
  SummaryFields,
  type SummaryText,
  splitSummary,
} from "@/components/tools/stats-tests/summary-fields";
import { TTestResults } from "@/components/tools/stats-tests/t-test-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberList } from "@/lib/parse-number";
import type {
  Alternative,
  NumberFormatter,
} from "@/lib/tool-kit/stats-tests/format";
import {
  independentTTest,
  oneSampleTTest,
  pairedTTest,
  type SampleSummary,
  summarize,
  type TTestOutcome,
} from "@/lib/tool-kit/stats-tests/t-test";

/** English strings. A translated content file overrides these through `ui`. */
export const T_TEST_CALCULATOR_STRINGS = {
  testLabel: "Test",
  oneSample: "One-sample",
  independent: "Independent samples",
  paired: "Paired samples",
  inputLabel: "Input",
  rawData: "Raw data",
  summaryStats: "Summary statistics",
  varianceLabel: "Variances",
  welch: "Welch (unequal variances)",
  student: "Student (pooled, equal variances)",
  sampleLabel: "Sample — numbers separated by commas, spaces, or new lines",
  sample1Label: "Sample 1 — numbers separated by commas, spaces, or new lines",
  sample2Label: "Sample 2 — numbers separated by commas, spaces, or new lines",
  firstLabel: "First measurement (e.g. before)",
  secondLabel: "Second measurement (e.g. after), in the same order",
  dataPlaceholder: "78, 85, 69, 91",
  valuesDetected: "one: value|other: values",
  group1: "Sample 1",
  group2: "Sample 2",
  meanLabel: "Mean (x̄)",
  sdLabel: "Standard deviation (s)",
  nLabel: "Sample size (n)",
  mu0Label: "Hypothesised mean (μ₀)",
  alternativeLabel: "Alternative hypothesis",
  twoSided: "Two-sided (≠)",
  less: "Less than (<)",
  greater: "Greater than (>)",
  alternativeHint:
    "Direction is sample 1 minus sample 2 (or the mean minus μ₀ in a one-sample test).",
  alphaLabel: "Significance level (α)",
  levelLabel: "Confidence level",
  tryExample: "Try an example",
  exampleOne: "5.1, 4.9, 5.6, 5.8, 6.0, 5.4, 5.2, 5.9, 6.1, 5.5",
  exampleMu0: "5",
  example1: "78, 85, 69, 91, 74, 88, 82, 79, 86, 72",
  example2: "71, 80, 65, 77, 68, 83, 74, 70, 76",
  exampleFirst: "72, 75, 68, 80, 77, 71, 74, 69, 78, 73",
  exampleSecond: "75, 78, 70, 83, 76, 75, 78, 72, 80, 77",
  exampleSummaryOne: "102.4|9.8|25",
  exampleSummaryMu0: "100",
  exampleSummary1: "10.2|2.1|15",
  exampleSummary2: "8.9|3.4|12",
  results: "Results",
  tStatistic: "t statistic",
  df: "Degrees of freedom",
  pValueFor: "p-value, {alternative}",
  meanDifference: "Mean difference",
  meanMinusMu0: "x̄ − μ₀",
  standardError: "Standard error",
  ciMean: "{level} CI for the mean",
  ciDifference: "{level} CI for the difference",
  cohensD: "Cohen's d",
  cohensDz: "Cohen's dz",
  pTableCaption: "p-values for each alternative hypothesis",
  alternativeColumn: "Alternative",
  exactColumn: "Exact p",
  apaColumn: "APA",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α. The difference is statistically significant.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α. The data are consistent with no difference — which is not proof of none.",
  howCalculated: "How this was calculated",
  differencesNote: "Differences are first − second:",
  apaHeading: "APA-style result",
  apaTemplate:
    "*t*({df}) = {t}, *p* {p}, *d* = {d}, {level} CI [{low}, {high}]",
  apaNoteOne:
    "The confidence interval is for the population mean; d = (x̄ − μ₀) / s.",
  apaNoteWelch:
    "The interval is for the mean difference. Welch's test does not pool the variances, so d divides by √((s₁² + s₂²) / 2), the root of their average.",
  apaNoteStudent:
    "The interval is for the mean difference; d divides by the pooled standard deviation sₚ.",
  apaNotePaired:
    "The interval is for the mean difference; d is dz, the mean difference divided by the standard deviation of the differences.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  promptRaw: "Enter at least two numbers in each sample to run the test.",
  promptSummary:
    "Enter a mean, a standard deviation, and a whole-number sample size of at least 2 for each sample.",
  promptPaired:
    "Enter at least two pairs: the same number of values in each box, in matching order.",
  errorLength:
    "Paired data must line up one to one, but the lists are {first} and {second} long.",
  errorVariance:
    "The data have no spread (a standard deviation of zero), so t cannot be computed.",
  errorSummary:
    "Check the summary statistics: the SD must be zero or more and n a whole number.",
} as const;

type Strings = typeof T_TEST_CALCULATOR_STRINGS;
type TestKind = "one" | "independent" | "paired";
type InputKind = "raw" | "summary";

interface FormState {
  test: TestKind;
  input: InputKind;
  welch: boolean;
  data1: string;
  data2: string;
  summary1: SummaryText;
  summary2: SummaryText;
  mu0: string;
  level: number;
}

/** The two samples as summaries, or null while the form is incomplete. */
function samplesFrom(form: FormState): SampleSummary[] | null {
  const count = form.test === "one" ? 1 : 2;
  if (form.input === "summary") {
    const list = [readSummary(form.summary1), readSummary(form.summary2)];
    const used = list.slice(0, count);
    return used.every((s) => s !== null && s.n >= 2)
      ? (used as SampleSummary[])
      : null;
  }
  const lists = [parseNumberList(form.data1), parseNumberList(form.data2)];
  const used = lists.slice(0, count);
  return used.every((values) => values.length >= 2)
    ? used.map(summarize)
    : null;
}

function runTest(form: FormState): TTestOutcome | null {
  if (form.test === "paired") {
    const first = parseNumberList(form.data1);
    const second = parseNumberList(form.data2);
    if (first.length < 2 && second.length < 2) {
      return null;
    }
    return pairedTTest(first, second, form.level);
  }
  const samples = samplesFrom(form);
  if (samples === null) {
    return null;
  }
  if (form.test === "one") {
    const mu0 = parseNumberInput(form.mu0);
    return mu0 === null ? null : oneSampleTTest(samples[0], mu0, form.level);
  }
  return independentTTest(samples[0], samples[1], !form.welch, form.level);
}

function promptFor(form: FormState, strings: Strings): string {
  if (form.test === "paired") {
    return strings.promptPaired;
  }
  return form.input === "summary" ? strings.promptSummary : strings.promptRaw;
}

function failureText(
  outcome: Extract<TTestOutcome, { ok: false }>,
  form: FormState,
  strings: Strings,
  fmt: NumberFormatter
): string {
  if (outcome.reason === "length-mismatch") {
    return t(strings.errorLength, {
      first: fmt(parseNumberList(form.data1).length),
      second: fmt(parseNumberList(form.data2).length),
    });
  }
  if (outcome.reason === "no-variance") {
    return strings.errorVariance;
  }
  if (outcome.reason === "invalid-summary") {
    return strings.errorSummary;
  }
  return promptFor(form, strings);
}

export default function TTestCalculatorWidget() {
  const strings = useToolUi(T_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [test, setTest] = useState<TestKind>("independent");
  const [input, setInput] = useState<InputKind>("raw");
  const [welch, setWelch] = useState(true);
  const [data1, setData1] = useState("");
  const [data2, setData2] = useState("");
  const [summary1, setSummary1] = useState<SummaryText>(EMPTY_SUMMARY);
  const [summary2, setSummary2] = useState<SummaryText>(EMPTY_SUMMARY);
  const [mu0, setMu0] = useState("0");
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [alpha, setAlpha] = useState("0.05");
  const [level, setLevel] = useState("0.95");

  const inputKind: InputKind = test === "paired" ? "raw" : input;
  const form: FormState = {
    test,
    input: inputKind,
    welch,
    data1,
    data2,
    summary1,
    summary2,
    mu0,
    level: Number(level),
  };
  const outcome = runTest(form);

  function fillExample() {
    if (test === "one") {
      setData1(strings.exampleOne);
      setSummary1(splitSummary(strings.exampleSummaryOne));
      setMu0(
        inputKind === "summary" ? strings.exampleSummaryMu0 : strings.exampleMu0
      );
      return;
    }
    if (test === "paired") {
      setData1(strings.exampleFirst);
      setData2(strings.exampleSecond);
      return;
    }
    setData1(strings.example1);
    setData2(strings.example2);
    setSummary1(splitSummary(strings.exampleSummary1));
    setSummary2(splitSummary(strings.exampleSummary2));
  }

  const count = (text: string) => {
    const size = parseNumberList(text).length;
    return `${fmt(size)}${tPlural(strings.valuesDetected, locale, size)}`;
  };
  const testOptions: Option<TestKind>[] = [
    { value: "one", label: strings.oneSample },
    { value: "independent", label: strings.independent },
    { value: "paired", label: strings.paired },
  ];
  const alternativeOptions: Option<Alternative>[] = [
    { value: "two-sided", label: strings.twoSided },
    { value: "less", label: strings.less },
    { value: "greater", label: strings.greater },
  ];
  const firstLabel = {
    one: strings.sampleLabel,
    independent: strings.sample1Label,
    paired: strings.firstLabel,
  }[test];
  const secondLabel =
    test === "paired" ? strings.secondLabel : strings.sample2Label;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.testLabel}
        name="tt-test"
        onChange={setTest}
        options={testOptions}
        value={test}
      />
      {test === "paired" ? null : (
        <PillGroup
          label={strings.inputLabel}
          name="tt-input"
          onChange={setInput}
          options={[
            { value: "raw", label: strings.rawData },
            { value: "summary", label: strings.summaryStats },
          ]}
          value={input}
        />
      )}
      {test === "independent" ? (
        <PillGroup
          label={strings.varianceLabel}
          name="tt-variance"
          onChange={(value) => setWelch(value === "welch")}
          options={[
            { value: "welch", label: strings.welch },
            { value: "student", label: strings.student },
          ]}
          value={welch ? "welch" : "student"}
        />
      ) : null}
      {inputKind === "raw" ? (
        <div
          className={test === "one" ? "space-y-4" : "grid gap-4 sm:grid-cols-2"}
        >
          <TextAreaField
            hint={count(data1)}
            id="tt-data-1"
            label={firstLabel}
            onChange={setData1}
            placeholder={strings.dataPlaceholder}
            value={data1}
          />
          {test === "one" ? null : (
            <TextAreaField
              hint={count(data2)}
              id="tt-data-2"
              label={secondLabel}
              onChange={setData2}
              placeholder={strings.dataPlaceholder}
              value={data2}
            />
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <SummaryFields
            labels={strings}
            onChange={setSummary1}
            prefix="tt-s1"
            title={test === "one" ? strings.summaryStats : strings.group1}
            value={summary1}
          />
          {test === "independent" ? (
            <SummaryFields
              labels={strings}
              onChange={setSummary2}
              prefix="tt-s2"
              title={strings.group2}
              value={summary2}
            />
          ) : null}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {test === "one" ? (
          <TextField
            id="tt-mu0"
            label={strings.mu0Label}
            numeric
            onChange={setMu0}
            value={mu0}
          />
        ) : null}
        <SelectField
          id="tt-alternative"
          label={strings.alternativeLabel}
          onChange={setAlternative}
          options={alternativeOptions}
          value={alternative}
        />
        <SelectField
          id="tt-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
        <SelectField
          id="tt-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={levelOptions(fmt)}
          value={level}
        />
      </div>
      <p className={HINT}>{strings.alternativeHint}</p>
      <ExampleButton label={strings.tryExample} onClick={fillExample} />
      {outcome?.ok ? (
        <TTestResults
          alpha={Number(alpha)}
          alternative={alternative}
          fmt={fmt}
          r={outcome.result}
          strings={strings}
        />
      ) : (
        <Notice>
          {outcome
            ? failureText(outcome, form, strings, fmt)
            : promptFor(form, strings)}
        </Notice>
      )}
    </div>
  );
}
